<script setup lang="ts">
import Button from '@/components/ui/button/Button.vue';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LazyTooltip, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/vue/24/outline';
import { computed, ref, watch, useId } from 'vue';
import { usePersistentTable } from '@/composables/usePersistentTable';

const props = withDefaults(
  defineProps<{
    totalRecords?: number;
    /**
     * Noun shown after the formatted total in the per-button tooltip
     * (e.g. "results", "tracks"). Keep this generic so the component stays
     * reusable across listing pages that share this header.
     */
    totalLabel?: string;
    /**
     * Opt into cursor (search_after) paging. The buttons then emit a semantic
     * `navigate` direction instead of a row offset, and the page/label uses the
     * externally supplied `currentPage` rather than deriving it from `pageIndex`
     * (which is meaningless once paging is cursor-based). Default is offset mode,
     * so the other tables that share this header are unaffected.
     */
    cursorMode?: boolean;
    /** 1-based page number, supplied by the parent in cursor mode. */
    currentPage?: number;
    /**
     * Cursor-mode navigation gates: whether the current response carried a next/prev token. These
     * (not the total, which can be an estimate / null / stale) drive Next/Last and Prev/First
     * enablement so paging can't overrun either end.
     */
    hasNext?: boolean;
    hasPrev?: boolean;
    /** Disable all navigation while a fetch is in flight (prevents replaying a stale token). */
    loading?: boolean;
  }>(),
  {
    totalLabel: 'results',
    cursorMode: false,
  },
);

const emit = defineEmits<{
  pagination: [pageIndex: number];
  navigate: [direction: 'first' | 'prev' | 'next' | 'last'];
}>();

const { setPageSize, pageIndex, pageSize } = usePersistentTable();
const pageSizeInputId = useId();

const size = computed(() => {
  const n = pageSize.value;
  return typeof n === 'number' && n > 0 ? n : 20;
});

const lastPage = computed(() => {
  const total = props.totalRecords ?? 0;
  const s = size.value;
  if (total <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(total / s));
});

const currentPage = computed(() => {
  // In cursor mode the page identity lives in the parent (the offset is meaningless), so use the
  // supplied page number; otherwise derive it from the store's offset as before.
  if (props.cursorMode) {
    return typeof props.currentPage === 'number' && props.currentPage > 0 ? props.currentPage : 1;
  }
  const s = size.value;
  const idx = typeof pageIndex.value === 'number' ? pageIndex.value : 0;
  return Math.floor(idx / s) + 1;
});

// First/Prev disable: in cursor mode on token availability (no prev token = at the start), in
// offset mode on the page bound; either way also while a fetch is in flight.
const isFirstPage = computed(
  () => props.loading || (props.cursorMode ? !props.hasPrev : currentPage.value <= 1),
);

// Next/Last disable: cursor mode keys off the next token (none = at the end), offset mode on the
// page bound; also while in flight.
const isLastPage = computed(
  () => props.loading || (props.cursorMode ? !props.hasNext : currentPage.value >= lastPage.value),
);

const totalCountFormatter = new Intl.NumberFormat();
const tooltipText = computed(() => {
  const totalCount = props.totalRecords ?? 0;
  return `${totalCountFormatter.format(totalCount)} ${props.totalLabel}`;
});

const pageSizeField = ref('20');
const paginationMenuOpen = ref(false);

watch(
  pageSize,
  (v) => {
    pageSizeField.value = String(typeof v === 'number' && v > 0 ? v : 20);
  },
  { immediate: true },
);

function applyPageSizeField() {
  const s = pageSizeField.value == null ? '' : String(pageSizeField.value);
  const n = parseInt(s.trim(), 10);
  if (isNaN(n) || n < 1) {
    pageSizeField.value = String(size.value);
    return;
  }
  setPageSize(Math.min(10000, n));
  paginationMenuOpen.value = false;
}

function goFirst() {
  if (props.cursorMode) {
    emit('navigate', 'first');
    return;
  }
  emit('pagination', 0);
}

function goPrev() {
  if (props.cursorMode) {
    emit('navigate', 'prev');
    return;
  }
  const s = size.value;
  const idx = typeof pageIndex.value === 'number' ? pageIndex.value : 0;
  emit('pagination', Math.max(0, idx - s));
}

function goNext() {
  if (props.cursorMode) {
    emit('navigate', 'next');
    return;
  }
  const s = size.value;
  const idx = typeof pageIndex.value === 'number' ? pageIndex.value : 0;
  const lastIdx = Math.max(0, (lastPage.value - 1) * s);
  emit('pagination', Math.min(lastIdx, idx + s));
}

function goLast() {
  if (props.cursorMode) {
    emit('navigate', 'last');
    return;
  }
  const s = size.value;
  emit('pagination', Math.max(0, (lastPage.value - 1) * s));
}
</script>

<template>
  <!-- On small screens the header's #right group wraps to its own row; there the
       pager spans the full width (justify-between spreads the controls edge to
       edge). At md+ it collapses back to a compact, content-width cluster. -->
  <div
    class="flex w-full flex-nowrap items-center justify-between gap-0 md:w-auto md:min-w-max md:shrink-0 md:justify-end"
    role="navigation"
    aria-label="Table pagination"
  >
    <!-- Each arrow's tooltip names what it does. A disabled arrow keeps its pointer
         events, which the shared button turns off, so on the first or last page the
         tooltip still says what the arrow would do. A disabled button still ignores
         clicks. The page menu's tooltip carries the record count. -->
    <Tooltip>
      <TooltipTrigger as-child>
        <Button
          variant="ghost"
          size="sm"
          class="h-7 rounded-lg w-7 shrink-0 p-0 disabled:pointer-events-auto disabled:cursor-default"
          data-testid="pagination-first"
          :disabled="isFirstPage"
          aria-label="First page"
          @click="goFirst"
        >
          <ChevronDoubleLeftIcon class="size-3.5" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>First page</TooltipContent>
    </Tooltip>
    <Tooltip>
      <TooltipTrigger as-child>
        <Button
          variant="ghost"
          size="sm"
          class="h-7 rounded-lg w-7 shrink-0 p-0 disabled:pointer-events-auto disabled:cursor-default"
          data-testid="pagination-previous"
          :disabled="isFirstPage"
          aria-label="Previous page"
          @click="goPrev"
        >
          <ChevronLeftIcon class="size-3.5" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Previous page</TooltipContent>
    </Tooltip>

    <!-- The tooltip wraps the whole menu, not just its trigger. See LazyTooltip for why. -->
    <LazyTooltip>
      <DropdownMenu v-model:open="paginationMenuOpen">
        <DropdownMenuTrigger as-child>
          <Button
            variant="ghost"
            size="sm"
            class="h-7 rounded-lg min-w-0 shrink-0 px-1.5 text-sm tabular-nums text-muted-foreground"
            data-testid="search-pagination-summary-trigger"
            :aria-label="
              `Page ${currentPage} of ${lastPage}. ${totalRecords ?? 0} records. Open to change rows per page.`
            "
          >
            {{ currentPage }} / {{ lastPage }}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" class="w-52">
          <div data-testid="search-pagination-menu" class="contents">
            <DropdownMenuLabel class="px-2 text-xs font-normal text-muted-foreground">
              Rows per page
            </DropdownMenuLabel>
            <div class="px-2 pb-2" @pointerdown.stop>
              <Input
                :id="pageSizeInputId"
                v-model="pageSizeField"
                data-testid="search-pagination-page-size"
                type="number"
                min="1"
                max="10000"
                aria-label="Rows per page (1-10000)"
                @blur="applyPageSizeField"
                @keydown.enter.prevent="applyPageSizeField"
              />
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <template #content>{{ tooltipText }}</template>
    </LazyTooltip>

    <Tooltip>
      <TooltipTrigger as-child>
        <Button
          variant="ghost"
          size="sm"
          class="h-7 rounded-lg w-7 shrink-0 p-0 disabled:pointer-events-auto disabled:cursor-default"
          data-testid="pagination-next"
          :disabled="isLastPage"
          aria-label="Next page"
          @click="goNext"
        >
          <ChevronRightIcon class="size-3.5" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Next page</TooltipContent>
    </Tooltip>
    <Tooltip>
      <TooltipTrigger as-child>
        <Button
          variant="ghost"
          size="sm"
          class="h-7 rounded-lg w-7 shrink-0 p-0 disabled:pointer-events-auto disabled:cursor-default"
          data-testid="pagination-last"
          :disabled="isLastPage"
          aria-label="Last page"
          @click="goLast"
        >
          <ChevronDoubleRightIcon class="size-3.5" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Last page</TooltipContent>
    </Tooltip>
  </div>
</template>
